import {
  Check,
  FileSearch,
  ListChecks,
  Loader2,
  Route,
  Sparkles,
} from "lucide-react";
import React, { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

interface PreparingGenerationProps {
  mode: "manual" | "automation";
  projectName?: string;
  pageCount?: number;
  interactionCount?: number;
  prompt?: string;
}

interface PrepStep {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint?: string;
}

/**
 * Informative "warming up" screen shown for the brief moment between the user
 * starting generation and the AI stream emitting its first event. Cycles through
 * the pipeline stages so the wait feels intentional and communicates what the
 * system is doing instead of an empty spinner.
 */
const PreparingGeneration: React.FC<PreparingGenerationProps> = ({
  mode,
  projectName,
  pageCount,
  interactionCount,
  prompt,
}) => {
  const steps: PrepStep[] =
    mode === "automation"
      ? [
          {
            icon: FileSearch,
            label: "Analyzing application structure",
            hint:
              pageCount && pageCount > 0
                ? `${pageCount} page${pageCount === 1 ? "" : "s"}${
                    interactionCount
                      ? ` · ${interactionCount} interaction${interactionCount === 1 ? "" : "s"}`
                      : ""
                  }`
                : "Mapping screens and elements",
          },
          {
            icon: Route,
            label: "Mapping critical user journeys",
            hint: "Happy paths, edge cases and negative flows",
          },
          {
            icon: Sparkles,
            label: "Drafting automation scenarios",
            hint: "Generating steps, selectors and assertions",
          },
          {
            icon: ListChecks,
            label: "Optimizing and de-duplicating",
            hint: "Tightening coverage before review",
          },
        ]
      : [
          {
            icon: FileSearch,
            label: "Reading your requirements",
            hint: prompt
              ? "Understanding intent and acceptance criteria"
              : "Understanding the context you provided",
          },
          {
            icon: Route,
            label: "Identifying test conditions",
            hint: "Positive, negative and boundary cases",
          },
          {
            icon: Sparkles,
            label: "Writing step-by-step cases",
            hint: "Clear actions with expected results",
          },
          {
            icon: ListChecks,
            label: "Reviewing for coverage",
            hint: "Removing gaps and overlaps",
          },
        ];

  const [activeStep, setActiveStep] = useState(0);

  // Advance forward through the pipeline and hold on the last stage. We never
  // loop back to step 0 — repeating would make the user think it's stuck in a
  // circle. The final stage keeps its spinner until real generation takes over.
  useEffect(() => {
    const id = setInterval(() => {
      setActiveStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
    }, 1400);
    return () => clearInterval(id);
  }, [steps.length]);

  // Workspace discovery items reveal one-by-one with an animated checkmark so it
  // reads like the AI is connecting to and mapping your application (Step 1).
  const discovery: { label: string; hint?: string }[] =
    mode === "automation"
      ? [
          { label: "Connected to application", hint: projectName || undefined },
          pageCount && pageCount > 0
            ? {
                label: `${pageCount} page${pageCount === 1 ? "" : "s"} discovered`,
              }
            : { label: "Discovering pages" },
          interactionCount && interactionCount > 0
            ? {
                label: `${interactionCount} interaction${interactionCount === 1 ? "" : "s"} mapped`,
              }
            : { label: "Mapping user journeys" },
        ]
      : [
          { label: "Workspace connected", hint: projectName || undefined },
          { label: prompt ? "Prompt received" : "Context received" },
          { label: "Requirements loaded" },
        ];

  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    if (revealed >= discovery.length) return;
    const id = setInterval(() => {
      setRevealed((r) => (r < discovery.length ? r + 1 : r));
    }, 650);
    return () => clearInterval(id);
  }, [revealed, discovery.length]);

  return (
    <div className="flex flex-1 min-h-0 flex-col items-center justify-center overflow-y-auto px-6 py-10">
      <div className="w-full max-w-[460px]">
        {/* Animated icon badge */}
        <div className="mb-5 flex justify-center">
          <div className="relative">
            <span className="absolute inset-0 -m-1 animate-ping rounded-2xl bg-info/20" />
            <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-info/15 to-info/5 ring-1 ring-info/20">
              <Sparkles className="h-6 w-6 animate-pulse text-info" />
            </div>
          </div>
        </div>

        {/* Title + subtitle */}
        <div className="mb-1 text-center text-[15px] font-semibold text-foreground">
          AI is preparing your workspace
        </div>
        <p className="mb-5 text-center text-[12.5px] leading-relaxed text-muted-foreground">
          Connecting to{" "}
          {projectName ? (
            <span className="font-medium text-foreground">{projectName}</span>
          ) : (
            "your project"
          )}{" "}
          and mapping{" "}
          {mode === "automation"
            ? "screens and journeys"
            : "requirements and context"}
          .
        </p>

        {/* Discovery — animated checkmarks */}
        <div className="mb-5 space-y-1.5">
          {discovery.map((item, idx) => {
            const done = idx < revealed;
            return (
              <div
                key={item.label}
                className={cn(
                  "flex items-center gap-2.5 rounded-md border px-3 py-2 transition-all duration-300",
                  done
                    ? "border-success/25 bg-success/[0.04] opacity-100"
                    : "border-transparent bg-transparent opacity-45",
                )}
              >
                <span
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300",
                    done
                      ? "bg-success/12 text-success"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {done ? (
                    <Check
                      className="h-3.5 w-3.5 animate-in zoom-in-50 duration-300"
                      strokeWidth={2.5}
                    />
                  ) : (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-medium text-foreground">
                    {item.label}
                  </div>
                  {item.hint && (
                    <div className="truncate text-[11px] text-muted-foreground">
                      {item.hint}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pipeline steps */}
        <div className="mb-6 space-y-1.5">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = idx === activeStep;
            const isDone = idx < activeStep;
            return (
              <div
                key={step.label}
                className={cn(
                  "flex items-center gap-3 rounded-md border px-3 py-2 transition-colors duration-300",
                  isActive
                    ? "border-info/30 bg-info/5"
                    : "border-transparent bg-transparent",
                )}
              >
                <span
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-300",
                    isActive
                      ? "bg-info/10 text-info"
                      : isDone
                        ? "bg-success/10 text-success"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {isActive ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : isDone ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div
                    className={cn(
                      "truncate text-[12.5px] font-medium transition-colors duration-300",
                      isActive || isDone
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    {step.label}
                  </div>
                  {step.hint && (
                    <div className="truncate text-[11px] text-muted-foreground">
                      {step.hint}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Skeleton preview of upcoming test cases */}
        <div className="space-y-2">
          <div className="px-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            Drafting test cases
          </div>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5"
              style={{ opacity: 1 - i * 0.22 }}
            >
              <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-muted" />
              <div className="flex-1 space-y-1.5">
                <div
                  className="h-2.5 animate-pulse rounded bg-muted"
                  style={{ width: `${70 - i * 12}%` }}
                />
                <div
                  className="h-2 animate-pulse rounded bg-muted/60"
                  style={{ width: `${45 - i * 8}%` }}
                />
              </div>
              <div className="h-5 w-12 shrink-0 animate-pulse rounded-full bg-muted" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreparingGeneration;
